import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { expertAuthApi, clientAuthApi } from '../lib/http';

interface AvailableProfiles {
  client: boolean;
  expert: boolean;
}

interface ProfileSwitcherProps {
  currentProfile: 'client' | 'expert';
  compact?: boolean;
}

const ProfileSwitcher: React.FC<ProfileSwitcherProps> = ({ currentProfile, compact = false }) => {
  const navigate = useNavigate();
  const [availableProfiles, setAvailableProfiles] = useState<AvailableProfiles | null>(null);
  const [isSwitching, setIsSwitching] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    const checkAvailableProfiles = async () => {
      try {
        if (currentProfile === 'expert') {
          const response = await expertAuthApi.session();
          setAvailableProfiles(response.data.available_profiles || null);
        } else {
          const response = await clientAuthApi.session();
          setAvailableProfiles(response.data.available_profiles || null);
        }
      } catch (error) {
        console.error('Failed to check available profiles:', error);
      }
    };

    checkAvailableProfiles();
  }, [currentProfile]);

  const canSwitch = availableProfiles &&
    (currentProfile === 'expert' ? availableProfiles.client : availableProfiles.expert);

  const handleSwitch = async () => {
    if (!canSwitch || isSwitching) return;

    setIsSwitching(true);

    // Store the role preference
    const targetRole = currentProfile === 'expert' ? 'client' : 'expert';
    localStorage.setItem('authRole', targetRole);

    // Navigate to the other profile's main page
    setTimeout(() => {
      if (targetRole === 'client') {
        navigate('/u');
      } else {
        navigate('/app/dashboard');
      }
      setIsSwitching(false);
    }, 300);
  };

  // Don't render if user only has one profile
  if (!canSwitch) {
    return null;
  }

  const targetProfile = currentProfile === 'expert' ? 'client' : 'expert';
  const isExpert = currentProfile === 'expert';

  // Compact version for mobile/tight spaces
  if (compact) {
    return (
      <button
        onClick={handleSwitch}
        disabled={isSwitching}
        className={`
          group relative flex items-center gap-2 px-3 py-2 rounded-xl
          transition-all duration-300 ease-out
          ${isSwitching ? 'opacity-50 cursor-wait' : 'cursor-pointer'}
          ${isExpert
            ? 'bg-purple-50 hover:bg-purple-100 text-purple-700'
            : 'bg-pink-50 hover:bg-pink-100 text-pink-700'
          }
        `}
      >
        {/* Switch Icon */}
        <svg
          className={`w-4 h-4 transition-transform duration-300 ${isSwitching ? 'animate-spin' : 'group-hover:rotate-180'}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
        </svg>

        <span className="text-sm font-medium">
          Switch to {targetProfile === 'expert' ? 'Expert' : 'Client'}
        </span>
      </button>
    );
  }

  // Full version with animation
  return (
    <div
      className="relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <button
        onClick={handleSwitch}
        disabled={isSwitching}
        className={`
          group relative flex items-center gap-3 w-full p-3 rounded-xl
          border-2 border-dashed transition-all duration-300 ease-out
          ${isSwitching ? 'opacity-60 cursor-wait' : 'cursor-pointer'}
          ${isExpert
            ? 'border-purple-200 hover:border-purple-400 hover:bg-purple-50/50'
            : 'border-pink-200 hover:border-pink-400 hover:bg-pink-50/50'
          }
        `}
      >
        {/* Target Profile Avatar */}
        <div className={`
          relative w-10 h-10 rounded-full flex items-center justify-center
          transition-all duration-300 ease-out
          ${isExpert
            ? 'bg-gradient-to-br from-purple-400 to-purple-600'
            : 'bg-gradient-to-br from-pink-400 to-rose-500'
          }
          ${isHovered ? 'scale-110' : 'scale-100'}
        `}>
          {/* Icon based on target profile */}
          {targetProfile === 'expert' ? (
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          ) : (
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          )}

          {/* Switching animation overlay */}
          {isSwitching && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/20 rounded-full">
              <svg className="w-5 h-5 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            </div>
          )}
        </div>

        {/* Text Content */}
        <div className="flex-1 text-left">
          <p className={`text-xs font-medium uppercase tracking-wider ${isExpert ? 'text-purple-400' : 'text-pink-400'}`}>
            Switch to
          </p>
          <p className={`text-sm font-semibold ${isExpert ? 'text-purple-700' : 'text-pink-700'}`}>
            {targetProfile === 'expert' ? 'Expert Mode' : 'Client Mode'}
          </p>
        </div>

        {/* Arrow Icon */}
        <div className={`
          flex items-center justify-center w-8 h-8 rounded-lg
          transition-all duration-300 ease-out
          ${isExpert ? 'bg-purple-100' : 'bg-pink-100'}
          ${isHovered ? 'translate-x-1' : 'translate-x-0'}
        `}>
          <svg
            className={`w-4 h-4 ${isExpert ? 'text-purple-600' : 'text-pink-600'}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </div>
      </button>

      {/* Decorative dots */}
      <div className={`
        absolute -top-1 -right-1 w-2 h-2 rounded-full
        transition-all duration-500
        ${isExpert ? 'bg-purple-400' : 'bg-pink-400'}
        ${isHovered ? 'opacity-100 scale-100' : 'opacity-0 scale-0'}
      `} />
      <div className={`
        absolute -bottom-1 -left-1 w-1.5 h-1.5 rounded-full
        transition-all duration-500 delay-75
        ${isExpert ? 'bg-purple-300' : 'bg-pink-300'}
        ${isHovered ? 'opacity-100 scale-100' : 'opacity-0 scale-0'}
      `} />
    </div>
  );
};

export default ProfileSwitcher;
